/**
 * Account page — protected. Shows two tabs:
 *   1. My Portraits — completed style_jobs created while logged in.
 *   2. My Orders — orders the user has placed.
 *
 * v1 keeps the UI lean; deeper drilldowns (per-job preview, reorder)
 * are linked through existing routes.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import SiteHeader from "../components/SiteHeader";
import Footer from "../components/Footer";
import { useAuth } from "../contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AccountPage() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("portraits");
  const [portraits, setPortraits] = useState([]);
  const [orders, setOrders] = useState([]);
  const [busy, setBusy] = useState(true);

  // Redirect unauthenticated users to the auth screen, preserving their
  // intent via a one-off toast on landing if desired.
  useEffect(() => {
    if (!loading && !user) navigate("/login", { replace: true });
  }, [loading, user, navigate]);

  useEffect(() => {
    if (!user) return;
    setBusy(true);
    Promise.all([
      axios.get(`${API}/auth/me/generations`).then((r) => r.data.items || []),
      axios.get(`${API}/auth/me/orders`).then((r) => r.data.items || []),
    ])
      .then(([gens, ords]) => {
        setPortraits(gens);
        setOrders(ords);
      })
      .catch(() => {})
      .finally(() => setBusy(false));
  }, [user]);

  if (loading || !user) return null;

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <SiteHeader />
      <main className="pt-24 pb-20 px-6 max-w-5xl mx-auto w-full flex-1">
        <div className="flex items-baseline justify-between flex-wrap gap-2 mb-6">
          <div>
            <h1 className="serif text-3xl text-[#1A1A1A]" data-testid="account-title">
              Hi, {user.name || user.email || user.phone}
            </h1>
            <p className="text-sm text-[#6B6B6B] mt-1">
              {user.email || user.phone}
            </p>
          </div>
          <button
            type="button"
            onClick={logout}
            className="text-sm text-[#9B6A2D] underline"
            data-testid="account-logout"
          >
            Log out
          </button>
        </div>

        {/* Tab switcher */}
        <div className="flex gap-2 border-b border-[#EAE5DF] mb-6">
          {[
            { id: "portraits", label: `Portraits (${portraits.length})` },
            { id: "orders", label: `Orders (${orders.length})` },
          ].map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={`px-4 py-2 text-sm border-b-2 -mb-px transition-colors ${
                tab === t.id
                  ? "border-[#C8A27A] text-[#1A1A1A]"
                  : "border-transparent text-[#6B6B6B] hover:text-[#1A1A1A]"
              }`}
              data-testid={`account-tab-${t.id}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {busy ? (
          <p className="text-sm text-[#6B6B6B]">Loading…</p>
        ) : tab === "portraits" ? (
          portraits.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-sm text-[#6B6B6B] mb-3">
                You haven&apos;t created any portraits yet.
              </p>
              <Link to="/" className="btn-accent inline-flex px-5 h-10 items-center">
                Create your first portrait
              </Link>
            </div>
          ) : (
            <div
              className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4"
              data-testid="account-portraits"
            >
              {portraits.map((j) => {
                const variant = (j.styles || [])[0];
                const img = variant?.preview_url || variant?.image_url;
                return (
                  <Link
                    key={j.job_id}
                    to={`/styles?job_id=${j.job_id}`}
                    className="bg-card border border-[#EAE5DF] hover:border-[#C8A27A] overflow-hidden block"
                    data-testid={`account-portrait-${j.job_id}`}
                  >
                    <div className="aspect-square bg-[#F3EEE6]">
                      {img ? (
                        <img src={img} alt={j.pet_name || "Portrait"} className="w-full h-full object-cover" loading="lazy" />
                      ) : null}
                    </div>
                    <div className="px-3 py-2">
                      <p className="text-sm font-medium text-[#1A1A1A] truncate">
                        {j.pet_name || "Pet portrait"}
                      </p>
                      <p className="text-xs text-[#6B6B6B]">
                        {new Date(j.created_at).toLocaleDateString("en-IN")}
                      </p>
                    </div>
                  </Link>
                );
              })}
            </div>
          )
        ) : orders.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-sm text-[#6B6B6B]">No orders yet.</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="account-orders">
            {orders.map((o) => (
              <div
                key={o.order_id}
                className="bg-card border border-[#EAE5DF] p-4 flex flex-wrap items-center justify-between gap-3"
                data-testid={`account-order-${o.order_id}`}
              >
                <div>
                  <p className="text-sm font-medium text-[#1A1A1A]">
                    {o.reference_number || o.order_id}
                  </p>
                  <p className="text-xs text-[#6B6B6B]">
                    {new Date(o.created_at).toLocaleDateString("en-IN")} ·{" "}
                    {o.status || "—"}
                  </p>
                </div>
                <p className="text-sm font-semibold text-[#1A1A1A]">
                  ₹{(o.total_amount || o.retail_price || 0).toLocaleString("en-IN")}
                </p>
              </div>
            ))}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
